import type { AdTagGroup } from "@services/engagements/commands/cmdGetEngagementAdTags"
import { inlineGroups } from "./adTags"
import "./adTags.scss"

type Props = {
  groups: AdTagGroup[] | undefined
  /** Show every group rather than just the two the dense views show. */
  all?: boolean
}

/*
    An ad's Content Catalog tags as inline chips (MPN-1506). Read-only everywhere it is
    used: tags are managed in the Content Catalog and only displayed here.

    An ad with none of the shown groups renders nothing at all. The schedule row and the
    monitor tile are compact, and an "unset" placeholder on every untagged ad would be
    louder than the tags it is standing in for.
*/
export function AdTagChips({ groups, all = false }: Props) {
  // Tolerant of a malformed group rather than throwing. These chips render inside a
  // schedule row and a monitor tile, so an exception here blanks the whole page — the
  // fetch is already guarded for the same reason, and the render has to match it.
  const usable = (groups ?? []).filter((g) => g && Array.isArray(g.tags))
  const shown = all ? usable : inlineGroups(usable)
  if (shown.length === 0) return null

  return (
    <span className="adtags">
      {shown.map((group) =>
        group.tags.map((tag) => (
          <span
            key={`${group.id}-${tag.id}`}
            className="adtags__chip"
            style={group.color ? tint(group.color) : undefined}
            title={group.name ? `${group.name}: ${tag.name}` : tag.name}
          >
            {tag.name}
          </span>
        )),
      )}
    </span>
  )
}

/* The group's colour is a full-strength hex meant for a swatch. Used as a chip background
   it swamps the row, so it tints the border and text and leaves the fill alone. */
function tint(color: string) {
  return { borderColor: color, color }
}
